import { useState, useEffect } from "react"
import { Table, Switch, Avatar, Typography, Input, Tag, Space, message } from "antd"
import { UserOutlined, SearchOutlined } from "@ant-design/icons"
import { collection, getDocs, doc, updateDoc, query, orderBy } from "firebase/firestore"
import { db } from "../../../firebase/config"

const { Text } = Typography

const ROLE_META = {
  admin: { label: "Admin", color: "red" },
  semiAdmin: { label: "Semi Admin", color: "volcano" },
  designer: { label: "Designer", color: "purple" },
  paperManager: { label: "Paper Manager", color: "blue" },
  accountant: { label: "Accountant", color: "green" },
  warehouse: { label: "Warehouse", color: "cyan" },
  winder: { label: "Winder", color: "gold" },
  arsomaUser: { label: "Arsoma", color: "magenta" },
  vivaUser: { label: "Viva", color: "lime" },
  tapeUser: { label: "Tape", color: "orange" },
  cutter: { label: "Cutter", color: "geekblue" },
  devSpecialist: { label: "Dev Specialist", color: "processing" },
  sales: { label: "Sales", color: "success" },
}

export default function OversightPermissionManager() {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [updating, setUpdating] = useState(new Set())

  useEffect(() => {
    fetchUsers()
  }, [])

  const fetchUsers = async () => {
    try {
      const snap = await getDocs(query(collection(db, "users"), orderBy("displayName")))
      setUsers(snap.docs.map((d) => ({ uid: d.id, ...d.data() })))
    } catch {
      message.error("Failed to load users")
    } finally {
      setLoading(false)
    }
  }

  const toggle = async (uid, current) => {
    setUpdating((prev) => new Set([...prev, uid]))
    try {
      await updateDoc(doc(db, "users", uid), { canOverseeUsers: !current })
      setUsers((prev) => prev.map((u) => (u.uid === uid ? { ...u, canOverseeUsers: !current } : u)))
      message.success(`Oversight ${!current ? "granted" : "revoked"}`)
    } catch {
      message.error("Update failed")
    } finally {
      setUpdating((prev) => {
        const s = new Set(prev)
        s.delete(uid)
        return s
      })
    }
  }

  const filtered = users.filter(
    (u) =>
      u.displayName?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase()),
  )

  const columns = [
    {
      title: "User",
      render: (_, r) => (
        <Space>
          <Avatar src={r.photoURL} icon={<UserOutlined />} size={32} />
          <div>
            <div>
              <Text strong>{r.displayName || "—"}</Text>
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {r.email}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Roles",
      render: (_, r) =>
        Object.entries(r.roles || {})
          .filter(([, v]) => v)
          .map(([k]) => (
            <Tag key={k} color={ROLE_META[k]?.color} style={{ fontSize: 10 }}>
              {ROLE_META[k]?.label || k}
            </Tag>
          )),
    },
    {
      title: "Can Oversee Users",
      align: "center",
      render: (_, r) => (
        <Switch
          checked={!!r.canOverseeUsers}
          loading={updating.has(r.uid)}
          onChange={() => toggle(r.uid, r.canOverseeUsers)}
          checkedChildren="Yes"
          unCheckedChildren="No"
        />
      ),
    },
  ]

  return (
    <>
      <Input
        placeholder="Search users…"
        prefix={<SearchOutlined />}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ maxWidth: 300, marginBottom: 16 }}
        allowClear
      />
      <Table
        columns={columns}
        dataSource={filtered}
        rowKey="uid"
        loading={loading}
        pagination={{ pageSize: 20 }}
        size="small"
      />
    </>
  )
}
